import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { login, reset, logout } from '../../features/auth/authSlice';
import CustomHeader from '../../components/layout/CustomHeader';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth
  );

  // Set background image on component mount
  useEffect(() => {
    document.body.style.background = `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/login.jpg')`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.overflow = 'auto';
    
    // Cleanup function to reset background when component unmounts
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundAttachment = '';
      document.body.style.overflow = '';
      // Remove any dashboard-body class that might have been added
      document.body.classList.remove('dashboard-body');
    };
  }, []);

  useEffect(() => {
    if (isError) {
      toast.error(message);
      dispatch(reset());
    }

    if (isSuccess || user) {
      setShowSuccess(true);
      toast.success('Login successful!');
      dispatch(reset());
    }
  }, [user, isError, isSuccess, message, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Reset error messages
    setErrors({});
    
    const { email, password, remember } = formData;
    let newErrors = {};
    
    // Basic validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const userData = {
      email,
      password,
    };

    dispatch(login(userData));
  };

  const goBack = () => {
    navigate(-1);
  };

  // const goToDashboard = () => {
  //   navigate('/dashboard');
  // };

  const goToHome = () => {
    navigate('/');
  };

  if (showSuccess) {
    const handleSignOut = () => {
      dispatch(logout());
      navigate('/login');
    };
    return (
      <>
        <CustomHeader activePage="login" />
        
        <button className="back-button" onClick={goBack}>
          <i className="fas fa-arrow-left"></i>
        </button>
        
        <div className="login-container">
          <div className="login-header">
            <img src="/logo.PNG" alt="IntelUniVen Logo" />
            <h1>Login Successful!</h1>
            <p>Welcome back, {user?.name}!</p>
          </div>
          
          <div className="success-options">
            {/* <button onClick={goToDashboard} className="btn-primary">
              Go to Dashboard
            </button> */}
            <button onClick={goToHome} className="btn-primary">
              Go to Home
            </button>
            <button onClick={handleSignOut} className="btn-secondary">
              Sign Out
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomHeader activePage="login" />
      
      <button className="back-button" onClick={goBack}>
        <i className="fas fa-arrow-left"></i>
      </button>
      
      <div className="login-container">
        <div className="login-header">
          <img src="/logo.PNG" alt="IntelUniVen Logo" />
          <h1>Welcome Back</h1>
          <p>Please login to your account</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <i className="fas fa-envelope"></i>
            <input 
              type="email" 
              name="email"
              placeholder="Email Address" 
              value={formData.email}
              onChange={handleChange}
              required 
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
          
          <div className="form-group">
            <i className="fas fa-lock"></i>
            <input 
              type="password" 
              name="password"
              placeholder="Password" 
              value={formData.password}
              onChange={handleChange}
              required 
            />
            {errors.password && <div className="error-message">{errors.password}</div>}
          </div>
          
          <div className="remember-forgot">
            <label className="remember-me">
              <input 
                type="checkbox" 
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
              />
              Remember me
            </label>
            <Link to="/forgot-password" className="forgot-password">
              Forgot Password?
            </Link>
          </div>
          
          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
          <div className="register-link">
            Don't have an account? <Link to="/register">Register here</Link>
          </div>
      </div>
    </>
  );
};

export default Login;